import { Suspense, useDeferredValue, useLayoutEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { ImperativePanelGroupHandle } from "react-resizable-panels"
import { DotsSixVertical } from "@phosphor-icons/react"
import { LoadingSpinner } from "@/components/loading-spinner"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable"
import { PatientLayoutInChat } from "@/layouts/patient-layout"
import { cn } from "@/lib/utils"
import { ChatEmpty } from "@/modules/chats/components/chat-layout/chat-empty"
import { ChatPanel } from "@/modules/chats/components/chat-layout/chat-panel"
import { ChatAppointmentEdge, ChatThreadList } from "@/modules/chats/components/chat-thread/chat-thread-list"
import { useChatThreadContext } from "@/modules/chats/stores/useChatThreadStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

type ChatThreadLayoutProps = { chatAppointmentEdges: ReadonlyArray<ChatAppointmentEdge> }

const TELECONSULTATION_DRAWER_NAME = "TeleconsultationDrawer"

// This value indicates max screen width from devices have resolution FHD and smaller
const FULL_HD_WIDTH = 1920

/**
 * Minimum allowable size of panel (numeric value between 1-100)
 *
 * {@link https://www.npmjs.com/package/react-resizable-panels See 'minSize' property from Panel}.
 */
const PANEL_SIZE = { MIN_HD_AND_SMALLER_WIDTH: 25, MIN_HD_AND_LARGER_WIDTH: 55 } as const
const MAX_PANEL_SIZE = 100

const resizeChatThreadPanel = (panelGroup: ImperativePanelGroupHandle) => {
  // automatically resize to show drawer fully when thread panel size larger than medical record panel
  const [threadPanelWidth] = panelGroup.getLayout()
  const isHDScreen = window.innerWidth <= FULL_HD_WIDTH
  const canResize = isHDScreen
    ? threadPanelWidth > PANEL_SIZE.MIN_HD_AND_SMALLER_WIDTH
    : threadPanelWidth > PANEL_SIZE.MIN_HD_AND_LARGER_WIDTH
  if (!canResize) return

  panelGroup.setLayout(
    isHDScreen
      ? [PANEL_SIZE.MIN_HD_AND_SMALLER_WIDTH, MAX_PANEL_SIZE - PANEL_SIZE.MIN_HD_AND_SMALLER_WIDTH]
      : [PANEL_SIZE.MIN_HD_AND_LARGER_WIDTH, MAX_PANEL_SIZE - PANEL_SIZE.MIN_HD_AND_LARGER_WIDTH],
  )
}

export const ChatThreadLayout = ({ chatAppointmentEdges }: ChatThreadLayoutProps) => {
  const { t } = useTranslation()

  const selectedThread = useChatThreadContext((state) => state.selectedThread)
  const drawerName = useDrawerStore((state) => state.drawerName)
  const panelGroupReference = useRef<ImperativePanelGroupHandle>(null)

  useLayoutEffect(() => {
    if (drawerName === TELECONSULTATION_DRAWER_NAME && panelGroupReference.current) {
      resizeChatThreadPanel(panelGroupReference.current)
    }
  }, [drawerName])

  const displayedThread = useDeferredValue(selectedThread)
  const isSwitchingThread = selectedThread?.id !== displayedThread?.id

  if (chatAppointmentEdges.length === 0)
    return <ChatEmpty title={t("chat.empty.title")} description={t("chat.empty.description")} />

  return (
    <ResizablePanelGroup direction="horizontal" autoSaveId="chat-panel-group" ref={panelGroupReference}>
      <ResizablePanel
        id="thread-panel"
        defaultSize={displayedThread ? 45 : 100}
        order={0}
        className="@container h-full w-full min-w-92.5">
        <div className="grid h-full grid-cols-[88px_1fr] grid-rows-1 transition-all duration-300 @md:grid-cols-[299px_1fr]">
          <div className="h-full border-r">
            <Suspense fallback={<LoadingSpinner isFullPage={true} size={24} className="bg-surface" />}>
              <div className="grid-rows-1fr-auto bg-surface grid h-full w-full">
                <div className="z-0 grid grid-rows-1">
                  <ChatThreadList chatAppointmentEdges={chatAppointmentEdges} selectedThread={selectedThread} />
                </div>
              </div>
            </Suspense>
          </div>
          <div
            className={cn(
              "h-full transition-opacity duration-200",
              isSwitchingThread && "pointer-events-none opacity-60",
            )}>
            {!displayedThread ? (
              <ChatEmpty title={t("chat.notSelected.title")} description={t("chat.notSelected.description")} />
            ) : (
              <Suspense fallback={<LoadingSpinner isFullPage isConversation size={24} className="bg-surface" />}>
                <ChatPanel selectedThread={displayedThread} />
              </Suspense>
            )}
          </div>
        </div>
      </ResizablePanel>
      <ResizableHandle disabled={!displayedThread} className="relative h-full w-px shrink-0">
        <div className="bg-surface-subdued text-on-surface-subdued absolute top-0 bottom-0 -left-2 z-1 m-auto flex h-fit w-fit items-center rounded-sm px-px py-0.5">
          <DotsSixVertical size={16} weight="thin" />
        </div>
      </ResizableHandle>
      {displayedThread ? (
        <ResizablePanel
          order={1}
          id="medical-record-panel"
          className="animate-fade-in-bottom @container flex h-full w-full min-w-80 overflow-x-auto! border-l-0 xl:min-w-3xl">
          <div
            className={cn(
              "flex h-full w-auto items-center justify-center rounded-lg border-none transition-opacity duration-200 xl:w-full [&>div:first-child]:h-full",
              isSwitchingThread && "pointer-events-none opacity-60",
            )}>
            <Suspense fallback={<LoadingSpinner isFullPage={true} size={24} />}>
              <PatientLayoutInChat
                key={displayedThread.id}
                appointmentId={displayedThread.appointmentId}
                country={displayedThread.patient.country}
                patientId={displayedThread.patient.id}
              />
            </Suspense>
          </div>
        </ResizablePanel>
      ) : (
        <ResizablePanel />
      )}
    </ResizablePanelGroup>
  )
}
